import React from "react";
import { Link } from "react-router-dom";

const Footer = React.forwardRef(({ compact = false, ...props }, ref) => (
  <footer 
    ref={ref}
    {...props}
    className="bg-black text-gray-300 border-t border-gray-800"
    role="contentinfo"
    itemScope
    itemType="https://schema.org/Organization"
  >
    <div className={compact ? "w-full px-5 py-3 md:px-6 md:py-4" : "w-full px-8 py-5"}>
      <div className={compact ? "grid grid-cols-2 md:grid-cols-[minmax(0,1.35fr)_minmax(0,0.75fr)_minmax(0,1fr)] gap-x-4 gap-y-4 mb-3" : "grid grid-cols-1 md:grid-cols-[minmax(0,1.35fr)_minmax(0,0.75fr)_minmax(0,1fr)] gap-8 mb-5"}>
        
        <div className={compact ? "col-span-2 space-y-2 md:col-span-1" : "space-y-4"}>
          <div>
            <h2 
              className="text-white font-medium text-lg mb-2"
              itemProp="name"
            >
              SIMPLYFAI INNOVATION (Pty) Ltd
            </h2>
            <div className={compact ? "space-y-1.5" : "space-y-2"}>
              <address 
                className={compact ? "not-italic text-xs leading-snug text-gray-400" : "not-italic text-sm leading-snug text-gray-400"}
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <span className="mr-2 font-medium text-gray-300">Johannesburg</span>
                <span itemProp="streetAddress">9th Floor, 5th St, Sandhurst</span>,{" "}
                <span itemProp="addressLocality">Sandton</span>,{" "}
                <span itemProp="postalCode">2196</span>,{" "}
                <span itemProp="addressCountry" content="ZA">South Africa</span>
              </address>

              <address 
                className={compact ? "not-italic text-xs leading-snug text-gray-400" : "not-italic text-sm leading-snug text-gray-400"}
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <span className="mr-2 font-medium text-gray-300">Cape Town</span>
                <span itemProp="streetAddress">Stanley &amp; Dock Rd</span>,{" "}
                <span>Cape Town City Centre</span>,{" "}
                <span itemProp="addressLocality">Cape Town</span>,{" "}
                <span itemProp="postalCode">8001</span>,{" "}
                <span itemProp="addressCountry" content="ZA">South Africa</span>
              </address>
            </div>
          </div>
          
        </div>

        <div className={compact ? "space-y-2" : "space-y-4"}>
          <h3 className="text-white font-medium text-base">Quick Links</h3>
          <nav className={compact ? "space-y-0.5" : "space-y-2"} role="navigation" aria-label="Footer navigation">
            <div className={compact ? "flex flex-col space-y-0.5 text-sm" : "flex flex-col space-y-2 text-sm"}>
              <Link 
                to="/about/" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="Learn more about us"
              >
                About
              </Link>
              <Link 
                to="/contact/" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="Contact us"
              >
                Contact
              </Link>
              <Link
                to="/evals/"
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="View agentic AI evaluations from Simplyfai"
              >
                Agentic AI Evals
              </Link>
              <Link 
                to="/privacy/" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="Read our privacy policy"
              >
                Privacy Policy
              </Link>
            </div>
          </nav>
        </div>

        <div className={compact ? "space-y-1.5" : "space-y-3"}>
          <div className="flex items-center gap-3">
            <h3 className="text-white font-medium text-base">Connect</h3>
            <a 
              href="https://www.linkedin.com/company/simplyfai/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-400 hover:text-white transition-colors duration-200"
              aria-label="Follow us on LinkedIn"
              itemProp="sameAs"
            >
              <svg className={compact ? "h-4 w-4" : "h-5 w-5"} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM0 8.98h5v14H0v-14zm7.5 0h4.78v1.92h.07c.66-1.24 2.28-2.55 4.69-2.55 5.01 0 5.93 3.3 5.93 7.59v8.04h-5v-7.12c0-1.7-.03-3.88-2.37-3.88-2.37 0-2.73 1.85-2.73 3.76v7.24h-5v-14z" />
              </svg>
            </a>
          </div>
          <div className={compact ? "space-y-1 text-sm" : "space-y-2 text-sm"}>
            <div className="flex items-baseline gap-2">
              <span className="w-[76px] shrink-0 text-xs text-gray-500 md:w-20">Johannesburg</span>
              <a 
                href="tel:+27100030800" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="Call our Johannesburg office at 010 003 0800"
              >
                <span itemProp="telephone">010 003 0800</span>
              </a>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="w-[76px] shrink-0 text-xs text-gray-500 md:w-20">Cape Town</span>
              <a 
                href="tel:+27214036300" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                aria-label="Call our Cape Town office at 021 403 6300"
              >
                <span itemProp="telephone">021 403 6300</span>
              </a>
            </div>
            <div className={compact ? "pt-0.5" : "pt-1"}>
              <a 
                href="mailto:maia@simplyfai.io" 
                className="text-gray-300 hover:text-white transition-colors duration-200 visited:text-gray-300"
                itemProp="email"
                aria-label="Email us at maia@simplyfai.io"
              >
                maia@simplyfai.io
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className={compact ? "pt-2 border-t border-gray-800" : "pt-4 border-t border-gray-800"}>
        <div className="text-left">
          <p className={compact ? "text-xs text-gray-400 leading-snug" : "text-base text-gray-400 leading-relaxed"}>
            © 2026 SIMPLYFAI INNOVATION (Pty) Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </footer>
));

Footer.displayName = 'Footer';

export default Footer;
